import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, FileText, Image, File, Square, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useDocuments } from '@/hooks/useDocuments';
import { MAX_FILE_SIZE, ALLOWED_FILE_TYPES } from '@/lib/types/completion';
import { formatBytes, formatTime, formatSpeed } from '@/lib/upload/formatters';
import { TUS_THRESHOLD } from '@/lib/upload/constants';
import { isUploadAbortedError } from '@/lib/upload';

interface FileUploadProps {
  entityType: 'job' | 'rfq' | 'bid' | 'completion' | 'specification';
  entityId: string;
  onUploadComplete?: () => void;
  multiple?: boolean;
  className?: string;
}

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
  isUploading?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  entityType,
  entityId,
  onUploadComplete,
  multiple = true,
  className = ''
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const { uploadDocument, validateFile, uploadProgress, cancelUpload, pauseUpload, resumeUpload } = useDocuments({ entityType, entityId });
  const { toast } = useToast();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setIsPaused(false);  // Reset pause state for new uploads
    
    const filesToUpload: UploadingFile[] = acceptedFiles.map(file => ({
      file,
      progress: 0
    }));

    setUploadingFiles(filesToUpload);

    for (let i = 0; i < filesToUpload.length; i++) {
      const fileUpload = filesToUpload[i];
      
      try {
        // Validate file
        const validationError = validateFile(fileUpload.file);
        if (validationError) {
          throw new Error(validationError);
        }

        // Mark as currently uploading
        setUploadingFiles(prev => prev.map((f, index) => 
          index === i ? { ...f, isUploading: true } : f
        ));

        await uploadDocument(fileUpload.file);
        
        setUploadingFiles(prev => prev.map((f, index) => 
          index === i ? { ...f, progress: 100, isUploading: false } : f
        ));

        toast({
          title: "File uploaded successfully",
          description: fileUpload.file.name,
        });
      } catch (error) {
        // Check if this was an intentional cancellation - skip error handling
        if (isUploadAbortedError(error)) {
          setUploadingFiles(prev => prev.filter((_, idx) => idx !== i));
          return;
        }
        
        setUploadingFiles(prev => prev.map((f, index) => 
          index === i ? { ...f, error: error instanceof Error ? error.message : 'Upload failed', isUploading: false } : f
        ));
        
        toast({
          title: "Upload failed",
          description: error instanceof Error ? error.message : 'Failed to upload file',
          variant: "destructive",
        });
      }
    }

    // Clear completed uploads after a delay
    setTimeout(() => {
      setUploadingFiles([]);
      onUploadComplete?.();
    }, 2000);
  }, [uploadDocument, validateFile, toast, onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxSize: MAX_FILE_SIZE
  });

  const removeUploadingFile = (index: number) => {
    const file = uploadingFiles[index];
    if (file?.isUploading) {
      cancelUpload();
    }
    setUploadingFiles(prev => prev.filter((_, i) => i !== index));
    setIsPaused(false);
  };

  const handlePauseResume = () => {
    if (isPaused) {
      resumeUpload();
      setIsPaused(false);
    } else {
      pauseUpload();
      setIsPaused(true);
    }
  };

  const handleCancelUpload = (index: number) => {
    cancelUpload();
    setUploadingFiles(prev => prev.filter((_, i) => i !== index));
    setIsPaused(false);
  };

  const isLargeFile = (file: File) => file.size >= TUS_THRESHOLD;

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (file.type === 'application/pdf') return <FileText className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  return (
    <div className={className}>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-muted-foreground/25 hover:border-primary/50'
          }`}
      >
        <input {...getInputProps()} />
        
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        
        {isDragActive ? (
          <p className="text-primary">Drop files here...</p>
        ) : (
          <div>
            <p className="text-foreground mb-1">
              Drag & drop files here, or click to select
            </p>
            <p className="text-sm text-muted-foreground">
              Max 200MB • PDF, JPG, PNG, DOCX, CSV, XLSX
            </p>
          </div>
        )}
      </div>

      {uploadingFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {uploadingFiles.map((fileUpload, index) => (
            <div key={index} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getFileIcon(fileUpload.file)}
                  <div className="flex-1">
                    <span className="text-sm font-medium truncate block">
                      {fileUpload.file.name}
                    </span>
                    {isLargeFile(fileUpload.file) && (
                      <span className="text-xs text-muted-foreground">
                        Resumable upload
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  {/* Pause/Resume button for large files */}
                  {fileUpload.isUploading && isLargeFile(fileUpload.file) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handlePauseResume}
                      className="h-6 w-6 p-0"
                      title={isPaused ? 'Resume upload' : 'Pause upload'}
                    >
                      {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileUpload.isUploading ? handleCancelUpload(index) : removeUploadingFile(index)}
                    className="h-6 w-6 p-0"
                    title={fileUpload.isUploading ? 'Cancel upload' : 'Remove'}
                  >
                    {fileUpload.isUploading ? <Square className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
              
              {fileUpload.error ? (
                <p className="text-sm text-destructive">{fileUpload.error}</p>
              ) : fileUpload.isUploading && uploadProgress ? (
                <div className="space-y-1">
                  <Progress value={uploadProgress.percentage} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>
                      {formatBytes(uploadProgress.bytesUploaded)} / {formatBytes(uploadProgress.bytesTotal)}
                    </span>
                    <div className="flex gap-2">
                      {uploadProgress.uploadSpeed && (
                        <span>{formatSpeed(uploadProgress.uploadSpeed)}</span>
                      )}
                      {uploadProgress.estimatedTimeRemaining && (
                        <span>~{formatTime(uploadProgress.estimatedTimeRemaining)} remaining</span>
                      )}
                    </div>
                  </div>
                  {isPaused && (
                    <p className="text-xs text-warning font-medium">Paused</p>
                  )}
                </div>
              ) : fileUpload.progress === 100 ? (
                <div className="space-y-1">
                  <Progress value={100} className="h-2" />
                  <p className="text-xs text-primary font-medium">Upload complete</p>
                </div>
              ) : (
                <div className="space-y-1">
                  <Progress value={0} className="h-2" />
                  <p className="text-xs text-muted-foreground">Preparing...</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};